import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable, BehaviorSubject } from 'rxjs';
import {
  EmployeesMealStatsResponse,
  Menu,
  RestaurantConfig,
  PunchesDataResponse,
  BlockedDate,
  BlockedDateCreate,
  BlockedDateUpdate,
  ReservationRecord,
  ReservationGuest,
  MealPrice,
  PostPrice,
  UpdatePaymentResponse,
  UpdatePunchChargeResponse,
  PostReservation,
} from '../../interfaces/restaurant.interface';
import { PunchJob, PunchSyncro } from '../../interfaces/utils.interface';

@Injectable({
  providedIn: 'root',
})
export class RestaurantService {
  // Modify the refresh mechanism to use a simple trigger without passing data
  private refreshStatsSubject = new BehaviorSubject<void>(undefined);

  constructor(private apiService: ApiService) {}

  // Deprecated - don't use this method directly as it may cause recursion
  refreshStats() {
    console.warn(
      'Using deprecated refreshStats() method. Use triggerStatsRefresh() instead.'
    );
    this.triggerStatsRefresh();
  }

  // New method to trigger a stats refresh without passing boolean data
  triggerStatsRefresh() {
    this.refreshStatsSubject.next();
  }

  // Method to subscribe to refresh events
  getRefreshStatsObservable(): Observable<void> {
    return this.refreshStatsSubject.asObservable();
  }

  // Aquesta funció de moment no es fa servir enlloc
  getGlobalStats() {
    return this.apiService.buildAPIPost<any[]>(
      '/restaurant/statistics/global/'
    );
  }

  getEmployeeStats(body?: any) {
    // Accepts an optional body for filtered_stats
    return this.apiService.buildAPIPost<EmployeesMealStatsResponse[]>(
      '/restaurant/stats/employees/',
      body || {}
    );
  }

  // Aquesta funció de moment no es fa servir enlloc
  getPunchesStats(body: any) {
    return this.apiService.buildAPIPost<any>('/punches/employees/stats/', body);
  }

  getPunchesData(body: any) {
    return this.apiService.buildAPIPost<PunchesDataResponse>(
      '/punches/employees/data/',
      body
    );
  }

  getPunchesKitchenData(body: any) {
    return this.apiService.buildAPIPost<PunchesDataResponse>(
      '/punches/employees/data/',
      body
    );
  }

  // Aquesta funció de moment no es fa servir enlloc
  getStats(externalId: string) {
    return this.apiService.buildAPIPost<any[]>(
      '/restaurant/statistics/user/' + externalId + '/'
    );
  }

  postBlockedDate(data: BlockedDateCreate) {
    return this.apiService.buildAPIPost<BlockedDate>(
      '/restaurant/blocked-dates/',
      data as any
    );
  }

  putBlockedDate(data: BlockedDateUpdate, id: string) {
    return this.apiService.buildAPIPut<BlockedDate>(
      '/restaurant/blocked-dates/',
      id,
      data as any
    );
  }

  unblockDate(data: BlockedDateUpdate, id: string) {
    return this.apiService.buildAPIPut<BlockedDate>(
      '/restaurant/blocked-dates/',
      id,
      data as any
    );
  }

  getReservations() {
    return this.apiService.buildAPIGet<ReservationRecord[]>(
      '/restaurant/reservations/'
    );
  }

  getBlockedDays() {
    return this.apiService.buildAPIGet<BlockedDate[]>(
      '/restaurant/blocked-dates/'
    );
  }

  // Aquesta funció de moment no es fa servir enlloc
  getBlockedDate(date: string) {
    return this.apiService.buildAPIGet<any>(
      `/restaurant/blocked-dates/${date}/`
    );
  }

  deleteRestaurant(id: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>(
      '/restaurant/reservations/',
      id
    );
  }

  getRestaurantPrices() {
    return this.apiService.buildAPIGet<MealPrice[]>('/restaurant/meal-prices/');
  }

  getRestaurantPrice(id: string) {
    return this.apiService.buildAPIGet<MealPrice>(
      '/restaurant/meal-prices/' + id + '/'
    );
  }

  postRestaurantPrice(data: PostPrice) {
    return this.apiService.buildAPIPost<PostPrice[]>(
      '/restaurant/meal-prices/',
      data as any
    );
  }

  putRestaurantPrice(data: PostPrice, id: string) {
    return this.apiService.buildAPIPut<PostPrice[]>(
      '/restaurant/meal-prices/',
      id,
      data as any
    );
  }

  deleteRestaurantPrice(id: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>('/restaurant/meal-prices/', id);
  }

  getRestaurantMenus() {
    return this.apiService.buildAPIGet<Menu[]>('/restaurant/menu-pdfs/');
  }

  postRestaurantMenu(data: any) {
    return this.apiService.buildAPIPost<Menu[]>('/restaurant/menu-pdfs/', data);
  }

  // Add delete menu function
  deleteRestaurantMenu(id: number): Observable<void> {
    // Ensure id is a number before passing to API
    return this.apiService.buildAPIDelete<void>(
      '/restaurant/menu-pdfs/',
      Number(id)
    );
  }

  postReservation(body: any): Observable<PostReservation> {
    return this.apiService.buildAPIPost<PostReservation>(
      '/restaurant/reservations/',
      body
    );
  }

  getReservation(id: number): Observable<ReservationRecord> {
    return this.apiService.buildAPIGet<ReservationRecord>(
      '/restaurant/reservations/' + id + '/'
    );
  }

  putReservation(id: number, body: any): Observable<PostReservation> {
    return this.apiService.buildAPIPut<PostReservation>(
      '/restaurant/reservations/',
      id,
      body
    );
  }

  getReservationGuests(id: number): Observable<ReservationGuest[]> {
    return this.apiService.buildAPIGet<ReservationGuest[]>(
      '/restaurant/reservations/' + id + '/guests/'
    );
  }

  postReservationGuest(
    reservationId: number,
    guestData: any
  ): Observable<ReservationGuest> {
    return this.apiService.buildAPIPost<ReservationGuest>(
      '/restaurant/reservations/' + reservationId + '/guests/',
      guestData
    );
  }

  getIntervalExtraMenus(
    start_date: string,
    end_date: string,
    dates: any[]
  ): Observable<any> {
    const body = {
      start_date: start_date,
      end_date: end_date,
      dates: dates,
    };
    return this.apiService.buildAPIPost<any>('/restaurant/config/', body);
  }

  getRestaurantConfig(date: string): Observable<RestaurantConfig> {
    return this.apiService.buildAPIGet<RestaurantConfig>(
      `/restaurant/config/?start_date=${date}`
    );
  }

  getRestaurantConfigByDate(date: string): Observable<RestaurantConfig> {
    return this.apiService.buildAPIGet<RestaurantConfig>(
      `/restaurant/config/?start_date=${date}`
    );
  }

  putRestaurantConfig(data: RestaurantConfig): Observable<RestaurantConfig> {
    return this.apiService.buildAPIPutRestaurantConfig<RestaurantConfig>(
      '/restaurant/config/',
      data as any
    );
  }

  updatePaymentStatus(
    reservationId: number,
    charge: boolean
  ): Observable<UpdatePaymentResponse> {
    return this.apiService.buildAPIPatch<UpdatePaymentResponse>(
      '/restaurant/',
      reservationId + '/payment-status',
      { charge: charge } as any
    );
  }

  updatePunchCharge(
    punchId: number,
    charge: boolean
  ): Observable<UpdatePunchChargeResponse> {
    return this.apiService.buildAPIPatch<UpdatePunchChargeResponse>(
      '/punches/',
      punchId + '/charge',
      { charge: charge } as any
    );
  }

  bulkUpdatePunchCharge(
    punchIds: number[],
    charge: boolean
  ): Observable<UpdatePunchChargeResponse[]> {
    const data = {
      punch_ids: punchIds,
      charge: charge,
    };
    return this.apiService.buildAPIPost<UpdatePunchChargeResponse[]>(
      '/punches/charge/',
      data
    );
  }

  private formatDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')}`;
  }

  syncPunches(startDate?: string, endDate?: string): Observable<PunchJob> {
    const today = this.formatDate(new Date());
    const start = startDate || today;
    const end = endDate || start;
    const data = {
      start_date: start,
      end_date: end,
    };
    // Use a broad request body type and cast the observable to the expected response type
    return this.apiService.buildAPIPost<any>(
      '/punches/sync/',
      data
    ) as Observable<PunchJob>;
  }

  syncPunchesKitchen(startDate: string, endDate: string): Observable<any> {
    const data = {
      start_date: startDate,
      end_date: endDate,
      job: false,
    };
    return this.apiService.buildAPIPost<any>(`/punches/sync/`, data);
  }

  getPunchesByDay(date: string): Observable<any> {
    const data = {
      date: date,
    };
    return this.apiService.buildAPIPost<any>(`/punches/all/`, data);
  }

  checkPunchJobStatus(jobUuid: string): Observable<{ status: string }> {
    return this.apiService.buildAPIGet<{ status: string }>(
      `/punches/sync/status/${jobUuid}/`
    );
  }

  sendRestaurantMenusByEmail(data: {
    date: string;
    menus: number;
    celiacs: number;
    guests: number;
    celiac_guests: number;
    celiac_menu_details: string[];
    extra: number;
    total: number;
  }): Observable<void> {
    return this.apiService.buildAPIPost<void>(
      '/restaurant/send-menu-email/',
      data
    );
  }

  postConfigDaily(data: {
    start_date: string;
    extra_menus: number;
    menus_sent?: boolean;
    sent_at?: string;
  }): Observable<void> {
    return this.apiService.buildAPIPost<void>(
      '/restaurant/config/',
      data as any
    );
  }
}
